import {
  triggersByObjectType,
  variableGroups,
  variablesMeta,
} from "./variablesMeta";

// Parameter 1 of a condition: the variable, and the step it reads from when it has one.
// The date, time and duration sub-forms rebuild this parameter, so the source travels with it
export const conditionVariableParameter = (model) => {
  const parameter = {
    variable: model?.variable,
    property_id: model?.property_id,
  };
  const contextStepId = model?.context_step_id;

  if (contextStepId !== null && contextStepId !== undefined) {
    parameter.context_step_id = contextStepId;
  }

  return parameter;
};

// The editor references a context step by its stable step id, the api by its position.
// Translating at the boundary means a step moving never invalidates a reference
const CONTEXT_ID_KEY = "context_step_id";
const CONTEXT_INDEX_KEY = "context_step_index";
const CONTEXT_ID_ATTRIBUTE = "data-context-step-id";
const CONTEXT_INDEX_ATTRIBUTE = "data-context-step-index";

/**
 * A reference whose step was deleted. Kept rather than dropped, so a saved automation still
 * says the source is gone instead of reading as a variable resolved against the trigger
 */
export const DELETED_CONTEXT_STEP = "DELETED_CONTEXT_STEP";
const DELETED_CONTEXT_STEP_INDEX = -1;

// Rewrite every context step reference an object holds, in nested objects and in the
// WYSIWYG content alike. A reference is never silently dropped, so it can never come to
// mean a different step
const translateNode = (
  node,
  fromKey,
  toKey,
  fromAttribute,
  toAttribute,
  translate,
) => {
  if (!node || typeof node !== "object") return;

  const chip = new RegExp(`\\s${fromAttribute}="([^"]*)"`, "g");

  Object.keys(node).forEach((key) => {
    const value = node[key];

    if (key === fromKey) {
      // The api sends the key with no reference in it, which is not a reference to step 0
      const empty = value === null || value === undefined || value === "";
      const translated = empty ? null : translate(value);
      delete node[fromKey];
      if (translated !== null) node[toKey] = translated;

      return;
    }

    if (typeof value === "string") {
      if (value.includes(fromAttribute)) {
        node[key] = value.replace(chip, (match, reference) => {
          const translated = translate(reference);

          return translated === null ? "" : ` ${toAttribute}="${translated}"`;
        });
      }

      return;
    }

    translateNode(value, fromKey, toKey, fromAttribute, toAttribute, translate);
  });
};

/**
 * Editor state -> api payload. Mutates the given steps, so hand it a copy.
 */
export const contextStepIdsToIndexes = (steps) => {
  const indexes = new Map(
    (steps || [])
      .map((step, index) => [step?.workflow_step_id, index])
      .filter(([id]) => Boolean(id)),
  );
  const translate = (id) =>
    indexes.has(id) ? indexes.get(id) : DELETED_CONTEXT_STEP_INDEX;

  (steps || []).forEach((step) =>
    translateNode(
      step,
      CONTEXT_ID_KEY,
      CONTEXT_INDEX_KEY,
      CONTEXT_ID_ATTRIBUTE,
      CONTEXT_INDEX_ATTRIBUTE,
      translate,
    ),
  );

  return steps;
};

/**
 * Api payload -> editor state. Mutates the given steps, so hand it a copy.
 */
export const contextStepIndexesToIds = (steps) => {
  const translate = (index) => {
    const position =
      typeof index === "number" ? index : Number.parseInt(index, 10);
    if (!Number.isInteger(position)) return null;

    return (steps || [])[position]?.workflow_step_id ?? DELETED_CONTEXT_STEP;
  };

  (steps || []).forEach((step) =>
    translateNode(
      step,
      CONTEXT_INDEX_KEY,
      CONTEXT_ID_KEY,
      CONTEXT_INDEX_ATTRIBUTE,
      CONTEXT_ID_ATTRIBUTE,
      translate,
    ),
  );

  return steps;
};

// Every context step this object references, from nested objects and WYSIWYG content alike
const collectContextStepIds = (node, found) => {
  if (!node || typeof node !== "object") return found;

  const chip = new RegExp(`\\s${CONTEXT_ID_ATTRIBUTE}="([^"]*)"`, "g");

  Object.keys(node).forEach((key) => {
    const value = node[key];

    if (key === CONTEXT_ID_KEY) {
      if (value) found.add(value);

      return;
    }

    if (typeof value === "string") {
      for (const match of value.matchAll(chip)) found.add(match[1]);

      return;
    }

    collectContextStepIds(value, found);
  });

  return found;
};

/**
 * Context steps a step points at that are no longer in the flow, so the reference has to
 * be re-pointed by hand. Deleting the step that produced the entity is how this happens.
 */
export const danglingContextStepIds = (step, steps) => {
  const known = new Set(
    (steps || []).map((s) => s?.workflow_step_id).filter(Boolean),
  );

  return [...collectContextStepIds(step, new Set())].filter(
    (id) => !known.has(id),
  );
};

// Entity types a step can produce, so a variable naming one resolves against a context step.
// Adding one here is only half the job, see the add-delay-trigger skill for every place to touch
const PRODUCIBLE_ENTITY_TYPES = ["DOCUMENT"];

// Variables the trigger itself provides need no context step, so they are never orphaned
const triggerVariables = (steps) => (steps || [])[0]?.variables || [];

const triggerProvidesGroup = (group, steps) =>
  (variableGroups[group]?.related_triggers || []).includes(
    (steps || [])[0]?.trigger_type,
  );

const needsContextStep = (variable, steps) => {
  const group = variablesMeta[variable]?.group;

  return (
    PRODUCIBLE_ENTITY_TYPES.includes(group) &&
    !triggerVariables(steps).includes(variable) &&
    !triggerProvidesGroup(group, steps)
  );
};

// A variable chip, whatever order its attributes were written in
const VARIABLE_CHIP = /<span[^>]*data-type="variable"[^>]*>/g;
const CHIP_VARIABLE = /data-id="([^"]*)"/;

const isSourced = (reference) => reference !== null && reference !== undefined;

// Every variable this object reads without naming a context step, from nested objects and
// WYSIWYG content alike. Steps straight from the api still name their source by index,
// which is a reference just as much as an id is
const collectUnsourcedVariables = (node, found) => {
  if (!node || typeof node !== "object") return found;

  Object.keys(node).forEach((key) => {
    const value = node[key];

    if (key === "variable") {
      const source = node[CONTEXT_ID_KEY] ?? node[CONTEXT_INDEX_KEY];
      if (value && !isSourced(source)) found.add(value);

      return;
    }

    if (typeof value === "string") {
      for (const chip of value.match(VARIABLE_CHIP) || []) {
        if (
          chip.includes(CONTEXT_ID_ATTRIBUTE) ||
          chip.includes(CONTEXT_INDEX_ATTRIBUTE)
        )
          continue;
        const variable = chip.match(CHIP_VARIABLE)?.[1];
        if (variable) found.add(variable);
      }

      return;
    }

    collectUnsourcedVariables(value, found);
  });

  return found;
};

/**
 * Variables this step reads from an entity a step produces, with no context step naming it.
 * The step that produced the entity was deleted, since committing drops the broken reference.
 */
export const orphanedEntityVariables = (step, steps) =>
  [...collectUnsourcedVariables(step, new Set())].filter((variable) =>
    needsContextStep(variable, steps),
  );

/**
 * Variables that only resolve through a context step, so a chip carrying none lost the step
 * it pointed at
 */
export const contextOnlyVariables = (steps) =>
  Object.keys(variablesMeta).filter((variable) =>
    needsContextStep(variable, steps),
  );

const CHIP_WITH_REFERENCE = new RegExp(
  `<span[^>]*data-id="([^"]*)"[^>]*\\s${CONTEXT_ID_ATTRIBUTE}="([^"]*)"`,
  "g",
);

// Point every reference a node holds at `translate(id)`, in place and without deleting a
// key, so a reactive copy keeps its shape and a null reference stays null
const rewriteReferences = (node, translate, onReference) => {
  if (!node || typeof node !== "object") return;

  const chip = new RegExp(`(\\s${CONTEXT_ID_ATTRIBUTE}=")([^"]*)(")`, "g");

  Object.keys(node).forEach((key) => {
    const value = node[key];

    if (key === CONTEXT_ID_KEY) {
      if (value !== null && value !== undefined && value !== "") {
        if (node.variable !== undefined) {
          onReference(value, node.variable, "condition");
        } else {
          onReference(value, node.trigger ?? null, "wait");
        }
        node[key] = translate(value);
      }
      return;
    }

    if (typeof value === "string") {
      if (value.includes(CONTEXT_ID_ATTRIBUTE)) {
        for (const match of value.matchAll(CHIP_WITH_REFERENCE)) {
          onReference(match[2], match[1], "chip");
        }
        node[key] = value.replace(
          chip,
          (match, open, reference, close) =>
            `${open}${translate(reference)}${close}`,
        );
      }
      return;
    }

    rewriteReferences(value, translate, onReference);
  });
};

/**
 * After a plan apply, point every step reference at the live step it means. `provisional`
 * maps each id this plan created or reminted to its live `workflow_step_id`, the same map
 * `findStepIndexById` reads, so it is looked up for every id and not only `NEW_*` ones.
 *
 * Returns a remapped copy (`changed` is false when nothing moved) and every `NEW_*`
 * reference nothing mapped, which would otherwise save as a deleted step.
 */
export const remapProvisionalContextStepReferences = (
  steps,
  provisional = {},
) => {
  const copy = JSON.parse(JSON.stringify(steps || []));
  const unmapped = [];
  let changed = false;

  copy.forEach((step, position) => {
    const translate = (id) => {
      const live = provisional[String(id)];
      if (live === null || live === undefined || String(live) === String(id)) {
        return id;
      }
      changed = true;
      return live;
    };
    const onReference = (id, variable, kind) => {
      const live = provisional[String(id)];
      if (
        String(id).startsWith("NEW_") &&
        (live === null || live === undefined)
      ) {
        unmapped.push({
          position,
          stepId: step.workflow_step_id || null,
          actionType: step.action_type || null,
          kind,
          variable,
        });
      }
    };
    rewriteReferences(step, translate, onReference);
  });

  return { steps: copy, changed, unmapped };
};

const referenceLabel = (key) =>
  variablesMeta[key]?.label ||
  Object.values(triggersByObjectType)
    .flat()
    .find((trigger) => trigger.value === key)?.label ||
  "the document variable";

// "SEND_EMAIL" -> "Send email", as optix-ai action_field_label writes it
const actionLabel = (actionType) => {
  const words = String(actionType || "Action")
    .replace(/_/g, " ")
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Handoff notes (the optix-ai canvas_handoff_notes shape) for references a plan apply left
// pointing at no step, worded like optix-ai's: `<step> — <what to do>`. Unstructured on
// purpose: the chip is still on the canvas, so a variable-keyed note would read as
// resolved and be pruned at once
export const unmappedReferenceNotes = (unmapped) =>
  (unmapped || []).map(({ position, stepId, actionType, kind, variable }) => {
    const label = referenceLabel(variable);
    const step =
      kind === "condition"
        ? "Condition"
        : kind === "wait"
          ? "Wait"
          : actionLabel(actionType);
    const verb = kind === "chip" ? "insert" : "choose";
    return {
      step_position: position,
      step_id: stepId,
      message: `${step} — ${verb} ${label} again from its document step`,
    };
  });
